import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';

export const DEFAULT_DELAY_REASONS = [
  { id: 'equipment', label: 'Equipment Issue' },
  { id: 'anesthesia', label: 'Anesthesia' },
  { id: 'patient', label: 'Patient' },
  { id: 'physician', label: 'Physician' },
  { id: 'staffing', label: 'Staffing Issue' },
  { id: 'other', label: 'Other' },
] as const;

export type DelayReasonOption = { id: string; label: string };

export const getDelayReasonLabel = (id: string, reasons: DelayReasonOption[]): string =>
  reasons.find(r => r.id === id)?.label ?? id;

export interface DelayReasonModalProps {
  onSubmit: (reason: string, otherDescription?: string) => void;
  onSkip: () => void;
  physicianName?: string;
  patientName?: string;
  gracePeriodMinutes?: number;
  delayDurationSeconds: number;
  delayReasons?: DelayReasonOption[];
  existingReason?: string | null;
  existingOtherDescription?: string | null;
}

const DEFAULT_GRACE_PERIOD_MINUTES = 5;

const formatElapsed = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');

  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(minutes)}:${pad(seconds)}`;
};

export const formatDelayDuration = formatElapsed;

const DelayReasonModal: React.FC<DelayReasonModalProps> = ({
  onSubmit,
  onSkip,
  physicianName,
  patientName,
  gracePeriodMinutes = DEFAULT_GRACE_PERIOD_MINUTES,
  delayDurationSeconds,
  delayReasons = DEFAULT_DELAY_REASONS as unknown as DelayReasonOption[],
  existingReason,
  existingOtherDescription,
}) => {
  const classes = useStyles();
  const [selectedReason, setSelectedReason] = useState<string | null>(existingReason ?? null);
  const [otherDescription, setOtherDescription] = useState(existingOtherDescription ?? '');
  const [errorMessage, setErrorMessage] = useState('');
  const [shakeKey, setShakeKey] = useState(0);
  const [dismissing, setDismissing] = useState(false);
  const [skipPhase, setSkipPhase] = useState<0 | 1>(0);

  const showError = () => {
    setErrorMessage('Please answer the question in order to proceed');
    setShakeKey(prev => prev + 1);
  };

  const isOtherReason = (reason: string | null) => reason?.toLowerCase() === 'other';

  const handleSelectReason = (reason: string) => {
    setSelectedReason(reason);
    setErrorMessage('');
    if (!isOtherReason(reason)) {
      setOtherDescription('');
    }
  };

  const handleSubmit = () => {
    if (!selectedReason) {
      showError();
      return;
    }

    if (isOtherReason(selectedReason) && otherDescription.trim() === '') {
      showError();
      return;
    }

    setDismissing(true);
    setTimeout(() => {
      onSubmit(selectedReason, isOtherReason(selectedReason) ? otherDescription.trim() : undefined);
    }, 400);
  };

  const hasError = !!errorMessage;
  const showOtherInput = isOtherReason(selectedReason);
  const otherInputEmpty = isOtherReason(selectedReason) && otherDescription.trim() === '';

  const shakeClass = shakeKey > 0 ? (shakeKey % 2 === 0 ? classes.shake2 : classes.shake1) : '';

  const paperClassName = [
    classes.paper,
    hasError ? classes.paperError : '',
    hasError ? shakeClass : '',
    dismissing ? classes.paperDismiss : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Dialog
      open
      maxWidth={false}
      onClose={(_event: object, reason: string) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          showError();
        }
      }}
      BackdropProps={{
        className: dismissing ? classes.backdropDismiss : '',
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        },
      }}
      PaperProps={{ className: paperClassName }}
    >
      <DialogContent className={classes.content}>
        <Typography variant="h4" align="center" gutterBottom style={{ fontWeight: 600, marginBottom: 16 }}>
          What is the reason for the delay?
        </Typography>

        <Box className={classes.infoBox}>
          <Box className={classes.infoRow}>
            <Box className={classes.infoItem}>
              <Typography variant="caption" className={classes.infoLabel}>
                Delay Duration
              </Typography>
              <Typography variant="h6" className={classes.timerValue}>
                {formatElapsed(delayDurationSeconds)}
              </Typography>
            </Box>
            <Box className={classes.infoItem}>
              <Typography variant="caption" className={classes.infoLabel}>
                Grace Period
              </Typography>
              <Typography variant="h6" className={classes.infoValue}>
                {gracePeriodMinutes} min
              </Typography>
            </Box>
          </Box>
          {(physicianName || patientName) && (
            <>
              <Box className={classes.infoDivider} />
              <Box className={classes.infoRow}>
                {physicianName && (
                  <Box className={classes.infoItem}>
                    <Typography variant="caption" className={classes.infoLabel}>
                      Physician
                    </Typography>
                    <Typography variant="body1" className={classes.infoValue}>
                      {physicianName}
                    </Typography>
                  </Box>
                )}
                {patientName && (
                  <Box className={classes.infoItem}>
                    <Typography variant="caption" className={classes.infoLabel}>
                      Patient
                    </Typography>
                    <Typography variant="body1" className={classes.infoValue}>
                      {patientName}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>

        {hasError && (
          <Typography variant="body1" className={classes.errorMessage}>
            {errorMessage}
          </Typography>
        )}

        <Box className={classes.optionsGrid}>
          {delayReasons.map(reason => (
            <Button
              key={reason.id}
              variant="outlined"
              className={`${classes.optionButton} ${selectedReason === reason.id ? classes.optionButtonSelected : ''}`}
              onClick={() => handleSelectReason(reason.id)}
              fullWidth
            >
              {reason.label}
            </Button>
          ))}
        </Box>

        {showOtherInput && (
          <TextField
            className={`${classes.textField} ${hasError && otherInputEmpty ? classes.textFieldError : ''}`}
            variant="outlined"
            multiline
            rows={3}
            fullWidth
            placeholder="Please describe the reason for the delay…"
            value={otherDescription}
            onChange={e => {
              setOtherDescription(e.target.value);
              if (e.target.value.trim()) {
                setErrorMessage('');
              }
            }}
            autoFocus
          />
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        {skipPhase === 0 && (
          <Box className={classes.buttonRow}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              className={classes.submitButton}
              onClick={handleSubmit}
            >
              Submit
            </Button>
            {/*<Button fullWidth className={classes.skipButton} onClick={() => setSkipPhase(1)}>*/}
            {/*  Skip for now*/}
            {/*</Button>*/}
          </Box>
        )}

        {skipPhase === 1 && (
          <Box style={{ width: '100%' }}>
            <Box className={classes.skipExplanation}>
              <Typography variant="body2" style={{ marginBottom: 8, lineHeight: 1.5 }}>
                Please enter the reason for the delay before the procedure ends.
              </Typography>
            </Box>
            <Box className={classes.buttonRow} style={{ marginTop: 12 }}>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                className={classes.submitButton}
                onClick={handleSubmit}
              >
                Submit
              </Button>
              <Button
                fullWidth
                className={classes.skipButton}
                onClick={() => {
                  setDismissing(true);
                  setTimeout(() => onSkip(), 400);
                }}
              >
                I understand
              </Button>
            </Box>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(2),
    maxWidth: 560,
    width: '90%',
    transition: 'border-color 0.3s ease',
  },
  paperError: {
    border: `2px solid ${theme.palette.error.main}`,
  },
  paperDismiss: {
    animation: '$flyToCorner 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards',
  },
  backdropDismiss: {
    animation: '$fadeOutBackdrop 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards',
  },
  '@keyframes flyToCorner': {
    '0%': {
      transform: 'translate(0, 0) scale(1)',
      opacity: 1,
    },
    '100%': {
      transform: 'translate(40vw, 40vh) scale(0.05)',
      opacity: 0,
    },
  },
  '@keyframes fadeOutBackdrop': {
    '0%': { opacity: 1 },
    '100%': { opacity: 0 },
  },
  shake1: {
    animation: '$shake1 0.5s ease',
  },
  shake2: {
    animation: '$shake2 0.5s ease',
  },
  '@keyframes shake1': {
    '0%, 100%': { transform: 'translateX(0)' },
    '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-6px)' },
    '20%, 40%, 60%, 80%': { transform: 'translateX(6px)' },
  },
  '@keyframes shake2': {
    '0%, 100%': { transform: 'translateX(0)' },
    '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-6px)' },
    '20%, 40%, 60%, 80%': { transform: 'translateX(6px)' },
  },
  content: {
    padding: theme.spacing(2, 2, 0),
  },
  optionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
  },
  optionButton: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    border: `2px solid rgba(255, 255, 255, 0.12)`,
    textTransform: 'none' as const,
    fontSize: '1.15rem',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.7)',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  optionButtonSelected: {
    borderColor: theme.palette.secondary.main,
    backgroundColor: `rgba(0, 167, 247, 0.15)`,
    color: theme.palette.secondary.light,
    '&:hover': {
      borderColor: theme.palette.secondary.main,
      backgroundColor: `rgba(0, 167, 247, 0.2)`,
    },
  },
  textField: {
    marginBottom: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      '& fieldset': {
        borderWidth: 2,
        borderColor: 'rgba(255, 255, 255, 0.12)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.secondary.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      color: theme.palette.text.primary,
    },
  },
  textFieldError: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.error.main,
      },
    },
  },
  actions: {
    padding: theme.spacing(1, 2, 2),
    flexDirection: 'column' as const,
    gap: 8,
  },
  buttonRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    width: '100%',
  },
  submitButton: {
    borderRadius: 12,
    padding: theme.spacing(1.5),
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none' as const,
    flex: 1,
  },
  skipButton: {
    borderRadius: 12,
    padding: theme.spacing(1.5),
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none' as const,
    flex: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    color: 'rgba(255, 255, 255, 0.6)',
    backgroundColor: 'transparent',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.4)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  errorMessage: {
    color: theme.palette.error.main,
    textAlign: 'center' as const,
    marginBottom: theme.spacing(1.5),
    fontWeight: 500,
  },
  infoBox: {
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  infoItem: {
    flex: 1,
    textAlign: 'center' as const,
  },
  infoLabel: {
    color: 'rgba(255, 255, 255, 0.45)',
    textTransform: 'uppercase' as const,
    letterSpacing: 1,
    fontSize: '0.65rem',
    fontWeight: 600,
    display: 'block',
    marginBottom: 4,
  },
  timerValue: {
    color: theme.palette.error.light,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: 1,
  },
  infoValue: {
    color: 'rgba(255, 255, 255, 0.85)',
    fontWeight: 500,
  },
  infoDivider: {
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    margin: theme.spacing(1.5, 0),
  },
  skipExplanation: {
    textAlign: 'center' as const,
    padding: theme.spacing(1, 2),
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.7)',
  },
}));

export default DelayReasonModal;
